// Lógica PURA das médias de avaliação. Ver `lib/badge-copy.ts` para o porquê de
// existir um arquivo separado: este módulo NÃO importa `mongodb` e não deve
// passar a importar. Componentes `"use client"` precisam de `hasRatings`,
// `formatAverage` e `avgStarsBlock` — e importar (mesmo só tipos) de um módulo
// que puxa o driver do Mongo faz o webpack tentar bundlar `net`/`tls`/`fs` para
// o browser. Texto e matemática ficam aqui; acesso ao banco, em
// `lib/rating-stats.ts`.

// Papel em que o AVALIADO recebeu a nota. Atenção ao inverso do campo `type`
// gravado no banco: `type` é o papel do AUTOR, então `type: "passenger"`
// (passageiro avaliando) é recebido pelo motorista COMO motorista.
export type RatingPersona = "driver" | "passenger"

export interface PersonaRatingStats {
  /** null quando não há nenhuma avaliação visível — nunca 0. Ver `hasRatings`. */
  average: number | null
  count: number
  /** Contagem por estrela, índice 0 = 1 estrela … índice 4 = 5 estrelas. */
  distribution: number[]
}

export interface UserRatingStats {
  asDriver: PersonaRatingStats
  asPassenger: PersonaRatingStats
}

// Uma avaliação fica escondida por 7 dias a não ser que a outra parte também
// avalie. AReciprocidade é o que impede o 1 estrela anônimo: você não pode
// afundar a reputação de alguém que ainda nem te conhece. Clicar em "avaliar"
// libera na hora.
export const RATING_VISIBILITY_DAYS = 7
export const RATING_VISIBILITY_MS = RATING_VISIBILITY_DAYS * 24 * 3600 * 1000

/**
 * Regra de VISIBILIDADE, morando na camada pura para que servidor, rota e teste
 * leiam a MESMA definição. Antes vivia em `lib/rating-utils.ts` e era
 * reimplementada inline em `app/api/profile/[id]/ratings/route.ts`.
 *
 * `auto: true` é sempre visível. Penalidade automática não é retaliação
 * anônima — é o sistema registrando um fato verificado (cancelamento em cima,
 * não comparecimento), e o usuário foi notificado quando aconteceu. Faz-lo
 * esperar mais 7 dias só faria a média demorar a refletir algo que já
 * aconteceu; o risco que a janela de reciprocidade combate não se aplica.
 */
export function isRatingVisible(rating: any, counterparts: any[], now: Date = new Date()): boolean {
  if (rating?.auto === true) return true
  const hasMutual = counterparts.some(
    (other: any) =>
      other.authorId === rating.targetId &&
      other.targetId === rating.authorId &&
      other.rideId === rating.rideId
  )
  if (hasMutual) return true
  const createdAt = new Date(rating.createdAt)
  if (Number.isNaN(createdAt.getTime())) return false
  return now.getTime() - createdAt.getTime() >= RATING_VISIBILITY_MS
}

// Rótulo das estrelas cheias, usado no detalhe e no perfil. A barra de
// distribuição é o lugar onde o número de estrelas se explica; aqui basta o
// ícone.
export const STAR_FILLED = "★"
export const STAR_EMPTY = "☆"

// ---------------------------------------------------------------------------
// JANELA DE AVALIAÇÃO
//
// Depois que a carona é liquidada (`settledAt`), o passageiro e o motorista
// têm 7 dias para se avaliar. A regra mora na camada pura para que a rota de
// API, o formulário de avaliação e o client leiam a MESMA definição — antes
// ela vivia em `lib/rating-utils.ts` (que importa `mongodb`, logo não pode
// entrar num componente client) e era reimplementada inline em
// `app/trip/[id]/rating/page.tsx` e `app/trip/[id]/TripDetailsContent.tsx`.
//
// Re-exportado de `lib/rating-utils.ts` para não quebrar os importadores.
// ---------------------------------------------------------------------------

export const RATING_WINDOW_DAYS = 7
export const RATING_WINDOW_MS = RATING_WINDOW_DAYS * 24 * 3600 * 1000

/** Fim da janela de avaliação da carona (ou null se ela não foi liquidada). */
export function ratingWindowEndsAt(ride: any): Date | null {
  if (!ride?.settledAt) return null
  const settled = new Date(ride.settledAt)
  if (Number.isNaN(settled.getTime())) return null
  return new Date(settled.getTime() + RATING_WINDOW_MS)
}

export function isRatingWindowExpired(ride: any, now: Date = new Date()): boolean {
  const endsAt = ratingWindowEndsAt(ride)
  if (!endsAt) return false
  return now.getTime() > endsAt.getTime()
}

export function isRatingWindowOpen(ride: any, now: Date = new Date()): boolean {
  // `ratingWindowEndsAt` devolve null quando a carona não liquidou OU quando o
  // `settledAt` é inválido — nos dois casos não há janela aberta. (O
  // `isRatingWindowExpired` sozinho não daria conta do `settledAt` corrompido:
  // ele devolveria "não expirada" e a janela pareceria aberta.)
  const endsAt = ratingWindowEndsAt(ride)
  if (!endsAt) return false
  return now.getTime() <= endsAt.getTime()
}

export function formatRatingWindowEndPtBr(ride: any): string | null {
  const endsAt = ratingWindowEndsAt(ride)
  if (!endsAt) return null
  return endsAt.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

export function emptyPersonaStats(): PersonaRatingStats {
  return { average: null, count: 0, distribution: [0, 0, 0, 0, 0] }
}

export function emptyUserRatingStats(): UserRatingStats {
  return { asDriver: emptyPersonaStats(), asPassenger: emptyPersonaStats() }
}

/**
 * Papel em que o ALVO recebeu uma avaliação de `type` — ou null se o `type` for
 * desconhecido. Retornar null (e não um default) é deliberado: um documento com
 * `type` corrompido não pode ser jogado numa persona qualquer, porque aí
 * contamina a média errada. `lib/rating-stats.ts` descarta o que retorna null.
 */
export function receivedAsPersona(type: unknown): RatingPersona | null {
  if (type === "passenger") return "driver"
  if (type === "driver") return "passenger"
  return null
}

// A API de criação valida inteiro entre 1 e 5, então valor fora disso é
// corrupção (ou migração antiga). Tratar como 0 — que é o que `Number(r ||
// 0)` fazia — faria a média exibir "0.0" para um usuário que tem avaliações
// porém nenhum dado válido. Descartar é mais honesto que inventar uma nota.
export function isValidRatingValue(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= 5
}

export function round1(value: number): number {
  return Math.round(value * 10) / 10
}

/**
 * Consome uma nota dentro de `distribution`. Devolve o índice usado para que o
 * chamador consiga somar a contagem com o valor exato, sem reler o array.
 *
 * `distribution` mutada de propósito: o agregado do Mongo já devolve um array,
 * e clonar a cada nota desperdiça memória numa lista com milhares de avaliações.
 */
export function accumulateDistribution(distribution: number[], value: number): number {
  const idx = value - 1
  if (idx < 0 || idx > 4) return 0
  distribution[idx] += 1
  return 1
}

export function finalizePersona(count: number, sum: number, distribution: number[]): PersonaRatingStats {
  return {
    // count 0 precisa ser `null` e não 0: "0.0 estrelas" é uma avaliação
    // péssima, "sem avaliações" é ausência de dado. O front depende disso para
    // não pintar um usuário sem histórico como se fosse mal avaliado.
    average: count > 0 ? round1(sum / count) : null,
    count,
    distribution: [...distribution],
  }
}

/**
 * Se há média a exibir. O usuário pediu explicitamente "mostrar sempre, sem base
 * mínima": não existe mais um piso de amostras nas estrelas. O badge de
 * cancelamento tem base mínima porque ali a taxa sozinha não quer dizer nada —
 * uma única queda não descreve ninguém. Para estrelas, uma única avaliação já é
 * opinião de quem enviou, e escondê-la só protege quem tem nota ruim.
 */
export function hasRatings(stats: PersonaRatingStats | null | undefined): stats is PersonaRatingStats {
  return !!stats && stats.count > 0 && stats.average !== null
}

/** "4.7" com ponto — o app formata com `toFixed(1)`. `null` vira "—". */
export function formatAverage(stats: PersonaRatingStats | null | undefined): string {
  // `typeof avg === "number"` e não `hasRatings(stats)`: o type guard estreita o
  // OBJETO, não a propriedade `average`, que continua `number | null` na
  // interface — então `stats.average.toFixed()` ainda seria `possibly null`.
  const avg = stats?.average
  return typeof avg === "number" ? avg.toFixed(1) : "—"
}

export function formatCount(stats: PersonaRatingStats | null | undefined): string {
  if (!hasRatings(stats)) return "sem avaliações"
  if (stats.count === 1) return "1 avaliação"
  return `${stats.count} avaliações`
}

/** "4.7 ★" para cards compactos. "—" quando não há dado. */
export function avgStarsBlock(stats: PersonaRatingStats | null | undefined): string {
  const formatted = formatAverage(stats)
  return formatted === "—" ? "—" : `${formatted} ${STAR_FILLED}`
}

/**
 * Quais personas têm dado a exibir. Vive aqui, e não dentro do componente, pelo
 * motivo do badge: `jest.config.js` só coleta `*.test.ts`, então uma decisão
 * tomada dentro de um `.tsx` fica sem cobertura nenhuma.
 *
 * A ordem é fixa (motorista antes de passageiro) para o resumo não trocar de
 * linha entre renders.
 */
export function visiblePersonas(input: {
  asDriver?: PersonaRatingStats | null
  asPassenger?: PersonaRatingStats | null
}): RatingPersona[] {
  const out: RatingPersona[] = []
  if (hasRatings(input.asDriver)) out.push("driver")
  if (hasRatings(input.asPassenger)) out.push("passenger")
  return out
}

/**
 * Normaliza um par (média, contagem) vindo do `User` denormalizado para o
 * formato de payload. `undefined` virou `null` e a contagem virou número, para
 * que o front nunca receba `undefined` (que se renderiza como "undefined" numa
 * interpolação) nem `NaN`.
 */
export function statsFromUserFields(avg: unknown, count: unknown): PersonaRatingStats {
  const average = typeof avg === "number" && Number.isFinite(avg) ? round1(avg) : null
  const parsed = typeof count === "number" && Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0
  return {
    average: average !== null && parsed > 0 ? average : null,
    count: parsed,
    distribution: [0, 0, 0, 0, 0],
  }
}

/**
 * Agrega avaliações JÁ FILTRADAS POR VISIBILIDADE em médias por papel.
 *
 * Existe como função pura separada para que a lista e a média venham
 * necessariamente do MESMO conjunto: a rota de avaliações filtra a lista e
 * precisa das médias, e recalcular por outro caminho (consulta nova, filtro
 * duplicado) criaria a possibilidade de a média e a lista discordarem — o tipo
 * de bug que só aparece em produção, quando uma avaliação destrava no meio da
 * janela de 7 dias.
 *
 * Descarta `type` desconhecido e nota fora de 1..5 (ver `receivedAsPersona` e
 * `isValidRatingValue`).
 */
export function aggregateByPersona(ratings: any[]): Map<string, UserRatingStats> {
  const acc = new Map<string, { count: number; sum: number; distribution: number[] }>()

  for (const r of ratings || []) {
    const userId = String(r?.targetId ?? "")
    const persona = receivedAsPersona(r?.type)
    if (userId.length === 0 || !persona) continue
    if (!isValidRatingValue(r.rating)) continue

    const key = `${userId}|${persona}`
    let entry = acc.get(key)
    if (!entry) {
      entry = { count: 0, sum: 0, distribution: [0, 0, 0, 0, 0] }
      acc.set(key, entry)
    }
    // `count` e `sum` avançam JUNTOS ou nenhum dos dois: somar a nota quando a
    // distribuição não a consumiu inflaria o dividendo sem inflar o divisor
    // (média puxada para baixo sem nenhuma avaliação a mais). Hoje
    // `isValidRatingValue` já garante 1..5, então os dois discordarem é
    // impossível — mas o par defensivo protege o caso em que essa guarda mudar.
    if (accumulateDistribution(entry.distribution, r.rating) === 1) {
      entry.count += 1
      entry.sum += r.rating
    }
  }

  const users = new Set<string>()
  for (const key of acc.keys()) users.add(key.slice(0, key.lastIndexOf("|")))

  const result = new Map<string, UserRatingStats>()
  for (const userId of users) {
    const asDriver = acc.get(`${userId}|driver`)
    const asPassenger = acc.get(`${userId}|passenger`)
    result.set(userId, {
      asDriver: asDriver ? finalizePersona(asDriver.count, asDriver.sum, asDriver.distribution) : emptyPersonaStats(),
      asPassenger: asPassenger
        ? finalizePersona(asPassenger.count, asPassenger.sum, asPassenger.distribution)
        : emptyPersonaStats(),
    })
  }
  return result
}
